/**
 * `artist.module.css` — the shared Artist / Tag layout. `docs/design-direction.md`
 * §8.6.
 *
 * `Artist.tsx` and `Tag.tsx` both render through `DetailView` (declared in
 * `Artist.tsx`, imported by `Tag.tsx`), so this one stylesheet is the whole visual
 * contract for both screens — there is no `tag.module.css`.
 *
 * `DetailView` renders through `<StateFrame grid="ledger">`, so `.ledger .body` in
 * `app/states/states.module.css` already owns the frame — deliberately with **no**
 * padding, because a 56px ledger row owns its own gutter (§5.2). Everything above the
 * ledger (the hero, the faceplate, the liner notes) is NOT a row, so `.head` restores
 * the page gutter for that block; the ledger rows below it stay edge-to-edge.
 *
 * **The one loud object is the stack** (`.stack`, from the `SpeakerStack` component —
 * its own file owns the flare and the bass-bin cutout; this file only sizes it).
 * **The one inverted block is `.playAll`** — nothing else on this screen fills solid
 * `--paper`.
 */

.screen {
  position: relative;
  inline-size: 100%;
}

/* ────────────────────────────────────────────────────────────────────────────────
   The hero block: the stack, the faceplate, the liner notes. This is the only part
   of the screen that needs the page gutter — the ledger below owns its own.
   ──────────────────────────────────────────────────────────────────────────────── */

.head {
  display: grid;
  gap: var(--s6);
  padding-inline: var(--gutter);
  padding-block-start: var(--s4);
  padding-block-end: var(--s6);
}

/* A plain heading wrapper: real `<h1>` semantics around the stack's own
   `role="img"` — the accessible name is computed from the nested image, so the
   heading is announced once, not twice. */
.heroWrap {
  margin: 0;
}

.stack {
  inline-size: 100%;
  max-inline-size: 420px;
}

/* ────────────────────────────────────────────────────────────────────────────────
   The amp-faceplate spec strip — four cells (fewer, on a tag: §12.6 omits rather
   than pads). Divided by 1px verticals, never a dash for an absent cell.
   ──────────────────────────────────────────────────────────────────────────────── */

.faceplate {
  display: flex;
  flex-wrap: wrap;
  border: 1px solid var(--border);
  background: var(--bg-raised);
  box-shadow: var(--raised-hi);
}

.faceplateCell {
  padding: var(--s3) var(--s4);
  border-inline-end: 1px solid var(--border);
  color: var(--text-2);
}

.faceplateCell:last-child {
  border-inline-end: none;
}

/* ────────────────────────────────────────────────────────────────────────────────
   The liner notes — prose locked to a 28px baseline (matches the paper ruling of
   §6.11, though this ground stays dark: §12.14 licenses literal cream paper in
   exactly two places, and this is not one of them), with the `--accent-tint`
   margin rule at `left:44px`. Absent entirely when there is nothing to print
   (§12.6): no heading, no placeholder rule, nothing.
   ──────────────────────────────────────────────────────────────────────────────── */

.prose {
  position: relative;
  padding-inline-start: calc(var(--drop) + var(--s3));
  display: grid;
  gap: var(--s2);
}

.prose::before {
  content: '';
  position: absolute;
  inset-block: 0;
  inset-inline-start: var(--drop);
  inline-size: 1px;
  background: var(--accent-tint);
}

.proseP {
  margin: 0;
  block-size: auto;
  line-height: 28px;
}

.sources {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: var(--s1);
}

.citation {
  color: var(--text-3);
  text-decoration: none;
}

.citation:hover,
.citation:focus-visible {
  text-decoration: underline;
}

/* ────────────────────────────────────────────────────────────────────────────────
   The DISCOGRAPHY sticky sub-header. `.playAll` is the screen's single inverted
   `--paper` block — nothing else here ever fills solid paper.
   ──────────────────────────────────────────────────────────────────────────────── */

.discoHeader {
  position: sticky;
  top: 0;
  z-index: var(--z-sticky);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s3);
  padding-inline: var(--gutter);
  padding-block: var(--s2);
  background: var(--bg);
  border-block-end: 1px solid var(--border-strong);
}

.discoHeaderHalo {
  display: inline-flex;
  align-items: center;
  padding-inline-end: var(--s3);
}

.discoTitle {
  color: var(--text);
}

.playAll {
  appearance: none;
  border: 1px solid var(--paper);
  border-radius: var(--r-cut);
  background: var(--paper);
  color: var(--ink-000);
  font: inherit;
  min-block-size: var(--drop);
  padding-inline: var(--s4);
  font-family: var(--ff-mono);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: var(--fs-13);
  cursor: pointer;
}

.playAll:active {
  background: var(--stock-2);
  border-color: var(--stock-2);
}

.playAll:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

/* ────────────────────────────────────────────────────────────────────────────────
   The DISCOGRAPHY ledger — a 5ch year gutter, the title, a right-aligned CAT №
   column, grouped by label under 11px caps sub-rules.
   ──────────────────────────────────────────────────────────────────────────────── */

.discList {
  margin: 0;
  padding: 0;
  list-style: none;
}

.discGroup {
  display: block;
}

.discGroupHeader {
  display: block;
  padding: var(--s2) var(--gutter);
  color: var(--text-3);
  border-block-end: 1px solid var(--border);
}

.discGroupRows {
  margin: 0;
  padding: 0;
  list-style: none;
}

.discRow {
  border-block-end: 1px solid var(--border);
  /* Reserved always, coloured only when current (§9.3's own pattern in
     `components.module.css`'s `.row`) — so the now-playing mark never shifts the
     row's own padding by a non-token 3px via `calc()`. */
  border-inline-start: 3px solid transparent;
}

.discButton {
  appearance: none;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  display: grid;
  grid-template-columns: 5ch 1fr auto;
  align-items: baseline;
  gap: var(--s3);
  inline-size: 100%;
  min-block-size: 44px;
  padding: var(--s2) var(--gutter);
  text-align: start;
  cursor: pointer;
}

.discRow[data-current='true'] {
  border-inline-start-color: var(--accent);
}

.discButton:active {
  background: var(--bg-pressed);
}

.discButton:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: -2px;
}

.discYear {
  color: var(--text-3);
}

.discTitle {
  color: var(--text);
  min-inline-size: 0;
}

.discCat {
  color: var(--text-2);
  text-align: end;
}

.loadMore {
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--text-2);
  font-family: var(--ff-mono);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  font-size: var(--fs-11);
  min-block-size: 44px;
  inline-size: 100%;
  padding-inline: var(--gutter);
  text-align: center;
  cursor: pointer;
}

.loadMore:disabled {
  color: var(--text-3);
  cursor: default;
}

.loadMore:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: -2px;
}

.error {
  margin: var(--s4) var(--gutter) 0;
}

/* Cyrillic degradation for this file's own mono-tier copy (§4.3, §12.16): tracked
   uppercase Cyrillic reads as shouting, so a Cyrillic value here keeps the tier and
   drops the case and the tracking, exactly as `primitives.module.css`'s `.cyr`
   does for its own components. */
.cyr {
  text-transform: none;
  letter-spacing: 0;
}
